import shortId from "shortid";

import { CartItem, CostObj } from "./types";

import { ClientTenant } from "~/tenant/types";
import { getVariantsPrice } from "~/product/selectors";
import { formatPrice } from "~/i18n/selectors";

const DELIVERY_METHOD_FIELD_ID = "default_delivery_mode";
const HOME_DELIVERY_TRANSLATION_KEY =
  "admin.config.customFields.card.deliveryMode.shipping";

export function getTotal(items: CartItem[]): number {
  return items.reduce((total, item) => total + getPrice(item) * item.count, 0);
}

export function getPrice(item: CartItem): number {
  switch (item.product.type) {
    // Price should not be modified
    case "hidden":
    case "unavailable":
    case "ask": {
      return 0;
    }

    // Price depends only on the variants
    case "variant": {
      return getVariantsPrice(item.variants);
    }

    // Sum total and variants
    default: {
      return item.product.price + getVariantsPrice(item.variants);
    }
  }
}

export const getCost = (fields): number => {
  const field = fields.find((field) => field.id === DELIVERY_METHOD_FIELD_ID);

  return field ? field.cost : 0;
};

export const optTotal = (newObj, total) => {
  const val = parseInt(newObj.amount) / 100;
  const mul = val * total;
  if (newObj.opt === "Recargo") {
    return mul.toFixed(2);
  } else {
    return mul.toFixed(2);
  }
};

export const optNominalTotal = (newObj: CostObj) => {
  if (newObj.opt === "Recargo") {
    return newObj.amount;
  } else {
    return newObj.amount;
  }
};

export const formatCost = (formValues): CostObj => {
  let newObj = {
    opt: "",
    amount: "",
    operation: "",
    payment: "",
  };
  Object.entries(formValues).filter((val) => {
    if (isText(val[1])) {
      if (includesFinancial(val[1]) && val[1].includes("default")) {
        const [opt, amount, operation, payment] = val[1].split("_");
        newObj = {
          opt,
          amount,
          operation,
          payment,
        };
      }
    }
  });

  return newObj;
};

export const getTotalPercentage = (
  amount: string,
  opt: string,
  total: number
) => {
  const val = parseInt(amount) / 100;
  const mul = val * total;

  return opt === "Recargo" ? total + mul : total - mul;
};

export const getTotalNominal = (amount: string, opt: string, total: number) => {
  const parseAmount = parseInt(amount);
  return opt === "Recargo" ? total + parseAmount : total - parseAmount;
};

export const addFinancialCost = (formValues, total) => {
  let { operation, amount, opt } = formatCost(formValues);

  const financialCost =
    operation === "nominal"
      ? getTotalNominal(amount, opt, total)
      : getTotalPercentage(amount, opt, total);

  return { financialCost, opt };
};

export const getFinancialCost = (formValues, total) => {
  let newObj = formatCost(formValues);

  const cost =
    newObj.operation === "percentage"
      ? optTotal(newObj, total)
      : optNominalTotal(newObj);

  return cost === "" ? 0 : parseFloat(cost);
};

export const hasDelivery = (formValues, t, fields) => {
  const deliveryIndex = fields.findIndex((field) => {
    return field.id === DELIVERY_METHOD_FIELD_ID;
  });
  const delivery = deliveryIndex >= 0 ? fields[deliveryIndex] : undefined;

  if (
    delivery &&
    !["Custo fixo", "Costo fijo", "Fixed cost"].includes(
      delivery?.options[1]?.selected
    )
  )
    return undefined;

  const selectedValue =
    formValues?.fields?.[deliveryIndex] ?? formValues?.[`fields[${deliveryIndex}]`];

  if (
    selectedValue === "home_delivery" ||
    selectedValue === t(HOME_DELIVERY_TRANSLATION_KEY)
  )
    return selectedValue;

  const [val] = Object.entries(formValues).filter((val) => {
    if (
      val[1] === "home_delivery" ||
      val[1] === t(HOME_DELIVERY_TRANSLATION_KEY)
    )
      return true;
    return false;
  });

  return val;
};

export const includesFinancial = (val) => {
  if (val.includes("Recargo") || val.includes("Descuento")) return true;
  return false;
};

export const hasFinancialCost = (formValues) => {
  const [val] = Object.entries(formValues).filter((val) => {
    if (isText(val[1])) {
      if (includesFinancial(val[1]) && val[1].includes("default"))
        return val[1];
    }
  });

  return val;
};

function isText(data: any): data is string {
  return typeof data === "string";
}

export const getFinancialText = (formValues) => {
  const opt: string[] = [];
  Object.entries(formValues).filter((val) => {
    if (isText(val[1])) {
      if (val[1].includes("Recargo") || val[1].includes("Descuento"))
        opt.push(val[1].split("_")[0]);
    }
  });

  return opt[0];
};

export function getFormattedPrice(
  item: CartItem,
  country?: string,
  hideDecimals?: boolean
): string {
  switch (item.product.type) {
    // This should never be shown
    case "hidden": {
      return "No disponible";
    }

    // No stock
    case "unavailable": {
      return "Sin stock";
    }

    // Ask price
    case "ask": {
      return "A consultar";
    }

    // Get price
    default: {
      return formatPrice(
        getPrice(item) * item.count,
        country,
        undefined,
        hideDecimals
      );
    }
  }
}

export function getCount(items: CartItem[]): number {
  return items.reduce((total, item) => total + item.count, 0);
}

/**
 * Counts how many times each option (by id) with handleStock appears
 * in the item's variant values.
 */
function getOptionOccurrences(item: CartItem): Record<string, number> {
  const occurrences: Record<string, number> = {};
  if (item.variants?.length) {
    for (const variant of item.variants) {
      if (variant.value?.length) {
        for (const option of variant.value) {
          if (option.handleStock) {
            occurrences[option.id] = (occurrences[option.id] || 0) + 1;
          }
        }
      }
    }
  }
  return occurrences;
}

/**
 * Calculates the effective max stock for a cart item considering both
 * product-level stock and variant option-level stock.
 * Accounts for the option occurrence multiplier: if the same option is
 * selected N times, each increment of count consumes N units of stock.
 * Returns undefined if no stock management applies.
 */
export function getItemMaxStock(item: CartItem): number | undefined {
  const stockLimits: number[] = [];

  // Product-level stock
  if (item.product.handleStock && Number.isInteger(item.product.currentStock)) {
    stockLimits.push(item.product.currentStock);
  }

  // Variant option-level stock (grouped by option id to account for multiplier)
  if (item.variants?.length) {
    const occurrences = getOptionOccurrences(item);
    const processed = new Set<string>();

    for (const variant of item.variants) {
      if (variant.value?.length) {
        for (const option of variant.value) {
          if (
            option.handleStock &&
            Number.isInteger(option.currentStock) &&
            !processed.has(option.id)
          ) {
            processed.add(option.id);
            const count = occurrences[option.id] || 1;
            stockLimits.push(Math.floor(option.currentStock! / count));
          }
        }
      }
    }
  }

  return stockLimits.length > 0 ? Math.min(...stockLimits) : undefined;
}

/**
 * Calculates the effective max stock for a cart item considering both
 * product-level stock, variant option-level stock, AND what other items
 * in the cart are already consuming of the same stock.
 * Accounts for the option occurrence multiplier.
 * Returns undefined if no stock management applies.
 */
export function getItemMaxStockInCart(
  item: CartItem,
  allCartItems: CartItem[]
): number | undefined {
  const stockLimits: number[] = [];

  // Product-level stock: subtract count of OTHER items with the same product
  if (item.product.handleStock && Number.isInteger(item.product.currentStock)) {
    const usedByOthers = allCartItems
      .filter(
        (other) => other.id !== item.id && other.product.id === item.product.id
      )
      .reduce((sum, other) => sum + other.count, 0);
    stockLimits.push(item.product.currentStock - usedByOthers);
  }

  // Variant option-level stock: account for multiplier and subtract others' usage
  if (item.variants?.length) {
    const occurrences = getOptionOccurrences(item);
    const processed = new Set<string>();

    for (const variant of item.variants) {
      if (variant.value?.length) {
        for (const option of variant.value) {
          if (
            option.handleStock &&
            Number.isInteger(option.currentStock) &&
            !processed.has(option.id)
          ) {
            processed.add(option.id);
            const selfOccurrences = occurrences[option.id] || 1;

            // Calculate stock used by OTHER cart items for this same option
            const usedByOthers = allCartItems
              .filter((other) => other.id !== item.id)
              .reduce((sum, other) => {
                const otherOccurrences = getOptionOccurrences(other);
                const otherCount = otherOccurrences[option.id] || 0;
                return sum + otherCount * other.count;
              }, 0);

            const availableStock = option.currentStock! - usedByOthers;
            stockLimits.push(Math.floor(availableStock / selfOccurrences));
          }
        }
      }
    }
  }

  if (stockLimits.length === 0) return undefined;
  return Math.max(0, Math.min(...stockLimits));
}

export const getOrderId = (slug: ClientTenant["slug"]) => {
  // Set characters
  shortId.characters(
    "0123456789abcdefghijklmnñopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZÑ"
  );

  // Generate order id - we replace ñ/Ñ caracters to prevent order page error.
  return `${slug.slice(0, 3).toUpperCase()}-${shortId
    .generate()
    .slice(0, 5)
    .replace("ñ", "n")
    .replace("Ñ", "N")
    .toUpperCase()}`;
};

export const displayProp = (price: number) => {
  return price > 0;
};
